import { logger } from "@/lib/logger";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import {
  Home,
  ArrowLeft,
  ArrowRight,
  Layers,
  Globe,
  FileText,
  BookOpen,
  Video,
  Users,
  FolderOpen,
} from "lucide-react";

const featuredResources = [
  {
    title: "BRIDGE Suite One-Pager",
    description: "Complete 3-module suite overview: BRIDGE™, SiteCommand™, and SiteLink™ in one printable page",
    icon: Layers,
    path: "/resources/bridge-suite-one-pager",
    badge: "One-Pager",
  },
  {
    title: "Africa Digitization Initiative",
    description: "How BRIDGE™ is transforming clinical trials across 54 African countries",
    icon: Globe,
    path: "/resources/africa-digitization-one-pager",
    badge: "One-Pager",
  },
  {
    title: "Sponsor Pitch Deck",
    description: "Comprehensive sponsor-facing pitch deck for partnership discussions",
    icon: FileText,
    path: "/resources/sponsor-pitch",
    badge: "Pitch",
  },
];

const quickLinks = [
  { label: "All Resources", path: "/resources", icon: BookOpen },
  { label: "Demo Booking", path: "/demo-booking", icon: Video },
  { label: "Trial Sites", path: "/trial-sites", icon: Users },
  { label: "Sponsor Portal", path: "/sponsor", icon: FolderOpen },
  { label: "Contact Support", path: "/contact", icon: Users },
];

export default function NotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    logger.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  if (!mounted) {
    return null;
  }

  return (
    <Layout>
      <div className="relative min-h-[80vh] overflow-hidden">
        {/* Floating orbs background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[10%] left-[15%] w-64 h-64 rounded-full bg-primary/5 blur-3xl animate-float" />
          <div className="absolute top-[30%] right-[10%] w-80 h-80 rounded-full bg-accent/5 blur-3xl animate-float animation-delay-200" />
          <div className="absolute bottom-[15%] left-[40%] w-72 h-72 rounded-full bg-primary/5 blur-3xl animate-float animation-delay-500" />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-12 max-w-6xl">
          {/* Hero section */}
          <div className="text-center mb-12">
            {/* Large 404 with gradient */}
            <div className="relative inline-block mb-6">
              <h1
                className="text-[8rem] md:text-[10rem] font-extrabold leading-none tracking-tighter bg-gradient-to-br from-primary via-primary/80 to-accent bg-clip-text text-transparent select-none"
                aria-label="404"
              >
                404
              </h1>
              {/* Blur halo */}
              <div className="absolute inset-0 -z-10 blur-3xl opacity-30 bg-gradient-to-br from-primary to-accent rounded-full scale-75" />
            </div>

            <h2 className="text-2xl md:text-3xl font-bold mb-3 text-foreground">
              This page has moved
            </h2>

            <p className="text-muted-foreground max-w-lg mx-auto mb-4">
              The content you were looking for has been relocated or reorganized.
              Here are a few paths that might get you back on track.
            </p>

            {/* Attempted path chip */}
            <code className="inline-block bg-muted px-3 py-1.5 rounded-full text-sm text-muted-foreground font-mono border border-border">
              {location.pathname}
            </code>
          </div>

          {/* Primary CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-14">
            <Button
              size="lg"
              onClick={() => navigate("/resources")}
              className="min-h-[48px] px-8"
            >
              <BookOpen className="mr-2 h-5 w-5" />
              Browse Resources
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/")}
              className="min-h-[48px] px-8"
            >
              <Home className="mr-2 h-5 w-5" />
              Go Home
            </Button>
          </div>

          {/* Featured resource cards */}
          <div className="mb-14">
            <h3 className="text-lg font-semibold text-center mb-6 text-foreground">
              Popular Resources
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredResources.map((resource) => {
                const Icon = resource.icon;
                return (
                  <Link key={resource.path} to={resource.path} className="block group">
                    <Card className="h-full hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-border/60">
                      <CardHeader>
                        <div className="flex items-start justify-between mb-2">
                          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                            <Icon className="h-6 w-6 text-primary" />
                          </div>
                          {resource.badge && (
                            <span className="text-xs px-2 py-1 bg-muted rounded-full text-muted-foreground">
                              {resource.badge}
                            </span>
                          )}
                        </div>
                        <CardTitle className="flex items-center justify-between text-base">
                          <span>{resource.title}</span>
                          <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                        </CardTitle>
                        <CardDescription>{resource.description}</CardDescription>
                      </CardHeader>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Quick link pills */}
          <Card className="max-w-3xl mx-auto bg-muted/30 border-border/40">
            <CardContent className="py-6 px-6">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider text-center mb-4">
                Quick Links
              </h3>
              <div className="flex flex-wrap justify-center gap-3">
                {quickLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Button
                      key={link.path}
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(link.path)}
                      className="rounded-full px-4 py-2 h-auto border-border/80 hover:bg-primary/5 hover:border-primary/30 transition-colors"
                    >
                      <Icon className="mr-1.5 h-4 w-4 text-muted-foreground" />
                      {link.label}
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Go back strip */}
          <div className="mt-10 text-center">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Or go back to where you were
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
